import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  DatePicker,
  Switch,
  App,
  Space,
  Typography,
  Spin,
  Divider,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getClientSpecialistById, updateClientSpecialist, updateClientStatus } from '../../services/clients';
import { lookupPostalCodeAddress } from '../../services/geo';
import { listSpecialties } from '../../services/catalogs';
import { getMembershipTypesDropdown } from '../../services/memberships';
import { createSubscription } from '../../services/subscriptions';

const { useApp } = App;
const { Title, Text } = Typography;

const ClientUpdateSpecialistPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { message, modal } = useApp();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [client, setClient] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [postalCodeData, setPostalCodeData] = useState(null);
  const [loadingPostalCode, setLoadingPostalCode] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [creatingSubscription, setCreatingSubscription] = useState(false);

  // Cargar especialidades y tipos de membresía
  useEffect(() => {
    Promise.all([
      listSpecialties(),
      getMembershipTypesDropdown(),
    ])
      .then(([specialtiesResponse, membershipTypesResponse]) => {
        // La respuesta puede venir como { specialties: [...] } o directamente como array
        const specialtiesData = specialtiesResponse?.specialties || specialtiesResponse?.data?.specialties || specialtiesResponse || [];
        setSpecialties(Array.isArray(specialtiesData) ? specialtiesData : []);
        
        // La respuesta de membershipTypes puede venir como array directo o dentro de data
        const membershipTypesData = membershipTypesResponse?.data || membershipTypesResponse || [];
        setMembershipTypes(Array.isArray(membershipTypesData) ? membershipTypesData : []);
      })
      .catch((error) => {
        console.error('Error al cargar opciones:', error);
        message.error('No se pudieron cargar las opciones');
      });
  }, [message]);

  // Cargar datos del especialista
  useEffect(() => {
    if (!id) return;

    setLoading(true);
    getClientSpecialistById(id)
      .then((response) => {
        const data = response.data || response;
        setClient(data);
        setIsActive(data.isActive);

        const { specialistProfile, address, activeSubscription: subscription } = data;

        // Guardar la suscripción activa
        setActiveSubscription(subscription);

        // Guardar el postalCodeId existente si hay address
        const existingPostalCodeId = address?.postalCode?.id;

        // Establecer valores del formulario
        form.setFieldsValue({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone?.replace(/^\+52/, '') || undefined,
          profile: 'Especialista',
          acceptsPrivacy: data.acceptsPrivacy ?? true,
          specialtyId: specialistProfile?.specialtyId || undefined,
          professionalLicense: specialistProfile?.professionalLicense || undefined,
          // Usar activeSubscription si existe, si no usar valores por defecto
          membershipTypeId: subscription?.membershipTypeId || undefined,
          membershipStartDate: subscription?.startDate 
            ? dayjs(subscription.startDate) 
            : undefined,
          membershipEndDate: subscription?.endDate 
            ? dayjs(subscription.endDate) 
            : undefined,
          postalCode: address?.postalCode?.cp || undefined,
          cityState: address?.postalCode
            ? `${address.postalCode.ciudad || address.postalCode.municipio}, ${address.postalCode.estado}`
            : undefined,
          street: address?.street || undefined,
          details: address?.details || undefined,
          postalCodeId: existingPostalCodeId,
        });

        // Si hay código postal pero no postalCodeId, cargar datos para obtenerlo
        if (address?.postalCode?.cp && !existingPostalCodeId) {
          loadPostalCodeData(address.postalCode.cp);
        }
      })
      .catch((error) => {
        console.error(error);
        const errorMessage = error?.__normalized?.message ?? 'No se pudo cargar el especialista.';
        message.error(errorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, form, message]);

  // Función para cargar datos del código postal
  const loadPostalCodeData = useCallback(async (cp) => {
    if (!cp || cp.length !== 5) {
      setPostalCodeData(null);
      form.setFieldsValue({ cityState: undefined, postalCodeId: undefined });
      return;
    }

    setLoadingPostalCode(true);
    try {
      const response = await lookupPostalCodeAddress(cp);
      const data = response.data || response;
      setPostalCodeData(data);
      
      // Si hay datos, establecer ciudad/estado
      // La respuesta es un array, tomar el primer elemento
      if (data && Array.isArray(data) && data.length > 0) {
        const firstState = data[0]; // Primer estado
        const firstMunicipio = firstState.municipios?.[0]; // Primer municipio
        if (firstMunicipio) {
          const cityState = `${firstMunicipio.ciudad || firstMunicipio.municipio}, ${firstState.estado}`;
          form.setFieldsValue({ 
            cityState,
            postalCodeId: firstMunicipio.idMunicipio,
          });
        }
      } else {
        form.setFieldsValue({ cityState: undefined, postalCodeId: undefined });
      }
    } catch (error) {
      console.error('Error al buscar código postal:', error);
      setPostalCodeData(null);
      form.setFieldsValue({ cityState: undefined, postalCodeId: undefined });
      message.warning('No se encontró información para este código postal');
    } finally {
      setLoadingPostalCode(false);
    }
  }, [form, message]);

  // Manejar cambio de código postal
  const handlePostalCodeChange = (e) => {
    const cp = e.target.value.replace(/\D/g, '').slice(0, 5);
    form.setFieldsValue({ postalCode: cp });
    
    if (cp.length === 5) {
      loadPostalCodeData(cp);
    } else {
      setPostalCodeData(null);
      form.setFieldsValue({ cityState: undefined, postalCodeId: undefined });
    }
  };

  // Manejar cambio de estado activo
  const handleStatusChange = async (checked) => {
    try {
      await updateClientStatus(id, checked);
      setIsActive(checked);
      message.success(`Cliente ${checked ? 'activado' : 'desactivado'} correctamente.`);
    } catch (error) {
      console.error(error);
      const errorMessage = error?.__normalized?.message ?? 'No se pudo actualizar el estado.';
      message.error(errorMessage);
      setIsActive(!checked); // Revertir cambio
    }
  };

  // Verificar si la suscripción está vencida
  const isSubscriptionExpired = () => {
    if (!activeSubscription || !activeSubscription.endDate) return true;
    return dayjs(activeSubscription.endDate).isBefore(dayjs(), 'day');
  };

  // Verificar si hay suscripción activa
  const hasActiveSubscription = () => {
    return activeSubscription && !isSubscriptionExpired();
  };

  // Manejar creación/renovación de suscripción
  const handleCreateSubscription = () => {
    const membershipTypeId = form.getFieldValue('membershipTypeId');
    
    if (!membershipTypeId) {
      message.warning('Debe seleccionar un tipo de membresía antes de generar la suscripción.');
      return;
    }

    const isExpired = isSubscriptionExpired();
    const hasActive = hasActiveSubscription();

    if (hasActive && !isExpired) {
      // Ya existe una suscripción activa
      modal.confirm({
        title: 'Renovar Suscripción',
        content: 'Ya existe una suscripción activa, ¿aún así desea renovarla?',
        okText: 'Sí, renovar',
        cancelText: 'Cancelar',
        onOk: () => createNewSubscription(membershipTypeId),
      });
    } else {
      // No existe o está vencida
      modal.confirm({
        title: 'Generar Suscripción',
        content: '¿Está seguro que desea generar la suscripción?',
        okText: 'Sí, generar',
        cancelText: 'Cancelar',
        onOk: () => createNewSubscription(membershipTypeId),
      });
    }
  };

  // Crear nueva suscripción
  const createNewSubscription = async (membershipTypeId) => {
    setCreatingSubscription(true);
    try {
      await createSubscription({
        clientId: Number(id),
        membershipTypeId: Number(membershipTypeId),
      });
      
      message.success('Suscripción generada correctamente.');
      
      // Recargar los datos del especialista para obtener la nueva suscripción
      const response = await getClientSpecialistById(id);
      const data = response.data || response;
      const { activeSubscription: newSubscription } = data;
      setActiveSubscription(newSubscription);
      
      // Actualizar los campos de membresía en el formulario
      if (newSubscription) {
        form.setFieldsValue({
          membershipTypeId: newSubscription.membershipTypeId,
          membershipStartDate: newSubscription.startDate 
            ? dayjs(newSubscription.startDate) 
            : undefined,
          membershipEndDate: newSubscription.endDate 
            ? dayjs(newSubscription.endDate) 
            : undefined,
        });
      }
    } catch (error) {
      console.error(error);
      const errorMessage = error?.__normalized?.message ?? 'No se pudo generar la suscripción.';
      message.error(errorMessage);
    } finally {
      setCreatingSubscription(false);
    }
  };

  // Enviar formulario
  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      // Obtener postalCodeId del formulario o de los datos cargados
      let postalCodeId = values.postalCodeId;
      if (!postalCodeId && postalCodeData && Array.isArray(postalCodeData) && postalCodeData.length > 0) {
        const firstState = postalCodeData[0];
        const firstMunicipio = firstState.municipios?.[0];
        if (firstMunicipio?.idMunicipio) {
          postalCodeId = firstMunicipio.idMunicipio;
        }
      }

      const payload = {
        specialtyId: values.specialtyId,
        professionalLicense: values.professionalLicense,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone ? (values.phone.startsWith('+52') ? values.phone : `+52${values.phone}`) : undefined,
        acceptsPrivacy: values.acceptsPrivacy ?? true,
        address: {
          postalCodeId: postalCodeId,
          street: values.street,
          details: values.details,
        },
      };

      await updateClientSpecialist(id, payload);
      message.success('Especialista actualizado correctamente.');
      navigate('/clients/all');
    } catch (error) {
      console.error(error);
      const errorMessage = error?.__normalized?.message ?? 'No se pudo actualizar el paciente.';
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/clients/all');
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              style={{ padding: 0, height: 'auto' }}
            >
              <Title level={3} style={{ margin: 0 }}>
                Editar Cliente - Especialista
              </Title>
            </Button>
          </div>
          <Switch
            checked={isActive}
            onChange={handleStatusChange}
            checkedChildren="Activo"
            unCheckedChildren="Inactivo"
            style={{ backgroundColor: isActive ? '#52c41a' : undefined }}
          />
        </div>

        {/* Formulario */}
        <Card>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
            initialValues={{
              acceptsPrivacy: true,
              profile: 'Especialista',
            }}
          >
            {/* Información Personal */}
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Nombre"
                  name="firstName"
                  rules={[{ required: true, message: 'Nombre requerido' }]}
                >
                  <Input placeholder="Nombre" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Apellidos"
                  name="lastName"
                  rules={[{ required: true, message: 'Apellidos requeridos' }]}
                >
                  <Input placeholder="Apellidos" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Correo electrónico"
                  name="email"
                  rules={[
                    { required: true, message: 'El correo es requerido' },
                    { type: 'email', message: 'Correo electrónico inválido' },
                  ]}
                >
                  <Input placeholder="correo@ejemplo.com" disabled />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Teléfono móvil"
                  name="phone"
                  rules={[
                    { pattern: /^\d{10}$/, message: 'Debe tener 10 dígitos' },
                  ]}
                >
                  <Input
                    placeholder="00000000000"
                    maxLength={10}
                    onChange={(e) => {
                      const onlyDigits = e.target.value.replace(/\D/g, '');
                      form.setFieldsValue({ phone: onlyDigits });
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item label="Perfil" name="profile">
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Código Postal"
                  name="postalCode"
                  rules={[
                    { required: true, message: 'El código postal es requerido' },
                    { pattern: /^\d{5}$/, message: 'Debe tener 5 dígitos' },
                  ]}
                >
                  <Input
                    placeholder="00000"
                    maxLength={5}
                    onChange={handlePostalCodeChange}
                    suffix={loadingPostalCode ? <Spin size="small" /> : null}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Ciudad/Estado" name="cityState">
                  <Input placeholder="Ciudad, Estado" disabled />
                </Form.Item>
              </Col>
            </Row>


            <Divider />
            {/* Información del Especialista */}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Especialidad"
                  name="specialtyId"
                  rules={[{ required: true, message: 'La especialidad es requerida' }]}
                >
                  <Select 
                    placeholder="Selecciona una especialidad"
                    options={specialties.map(s => ({
                      value: s.id,
                      label: s.label || s.displayName || s.name,
                    }))}
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Cédula Profesional"
                  name="professionalLicense"
                  rules={[{ required: true, message: 'La cédula profesional es requerida' }]}
                >
                  <Input placeholder="12345678" disabled />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Tipo de Membresía"
                  name="membershipTypeId"
                  rules={[{ required: true, message: 'El tipo de membresía es requerido' }]}
                >
                  <Select 
                    placeholder="Selecciona un tipo de membresía"
                    options={Array.isArray(membershipTypes) ? membershipTypes.map(m => ({
                      value: m.id,
                      label: m.name || m.label,
                    })) : []}
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label=" ">
                  <Button
                    type="default"
                    onClick={handleCreateSubscription}
                    loading={creatingSubscription}
                    style={{ width: '100%' }}
                  >
                    {hasActiveSubscription() ? 'Renovar Suscripción' : 'Generar Suscripción'}
                  </Button>
                </Form.Item>
              </Col>

              </Row>
              <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Fecha Inicio de Membresía"
                  name="membershipStartDate"
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    placeholder="DD/MM/YYYY"
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Fecha Fin de Membresía"
                  name="membershipEndDate"
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    placeholder="DD/MM/YYYY"
                    disabled
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Campo oculto para postalCodeId */}
            <Form.Item name="postalCodeId" hidden>
              <Input />
            </Form.Item>

            {/* Botones */}
            <Row>
              <Col>
                <Button type="primary" htmlType="submit" loading={submitting}>
                  Aceptar
                </Button>
              </Col>
            </Row>
          </Form>
        </Card>
      </Space>
    </div>
  );
};

export default ClientUpdateSpecialistPage;

